import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Modal,
  Radio,
  Slide,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import {ListQuestion} from "models/managerTest";
import React, {useMemo, useState} from "react";
import {isLimitedDisplay, pickDisplayedQuestions} from "utils/examPreview";

export interface ExamStudentPreviewModalProps {
  open: boolean;
  onClose: () => void;
  questions: ListQuestion[];
  displayCount?: number | null;
  examTitle?: string;
  isEssay?: boolean;
}

export default function ExamStudentPreviewModal({
  open,
  onClose,
  questions,
  displayCount,
  examTitle,
  isEssay,
}: ExamStudentPreviewModalProps) {
  const [shuffleKey, setShuffleKey] = useState(0);

  const previewQuestions = useMemo(
    () => pickDisplayedQuestions(questions, displayCount),
    [questions, displayCount, shuffleKey]
  );

  const limited = isLimitedDisplay(displayCount ?? null, questions.length);

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Slide direction="up" in={open}>
        <Box
          style={{
            position: "absolute",
            top: "5%",
            left: "50%",
            transform: "translateX(-50%)",
            width: "92%",
            maxWidth: 900,
            maxHeight: "90vh",
            overflowY: "auto",
            backgroundColor: "#f5f5f5",
            borderRadius: 12,
            padding: "16px 24px 24px",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" style={{fontWeight: 700, color: "#004673"}}>
              Xem thử như học viên
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {examTitle && (
            <Typography variant="subtitle2" style={{marginBottom: 8, fontWeight: 600}}>
              {examTitle}
            </Typography>
          )}

          <Box
            mb={2}
            p={1.5}
            style={{
              backgroundColor: limited ? "#E8F4FD" : "#FFF8E6",
              borderRadius: 8,
              border: `1px solid ${limited ? "#137BBD" : "#F5A623"}`,
            }}
          >
            <Typography variant="body2">
              {limited ? (
                <>
                  Hiển thị <strong>{previewQuestions.length}</strong> / {questions.length} câu (ngẫu
                  nhiên). Mỗi học viên hoặc mỗi lần bấm &quot;Shuffle lại&quot; sẽ thấy bộ câu khác
                  nhau.
                </>
              ) : (
                <>
                  Hiển thị <strong>tất cả {questions.length} câu</strong>. Nhập số câu ngẫu nhiên
                  (nhỏ hơn tổng số câu) để xem thử chế độ giới hạn.
                </>
              )}
            </Typography>
            {limited && (
              <Box mt={1}>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => setShuffleKey((k) => k + 1)}
                >
                  Shuffle lại
                </Button>
              </Box>
            )}
          </Box>

          {previewQuestions.map((item, index) => (
            <Box
              key={`${item.id ?? index}-${shuffleKey}`}
              mt={2}
              p={2}
              style={{
                backgroundColor: "#fff",
                borderRadius: 6,
                boxShadow: "0px 2px 4px rgba(168, 168, 168, 0.25)",
              }}
            >
              <Typography variant="subtitle2" style={{color: "#0065A6", marginBottom: 8}}>
                Câu {index + 1}: {item.questionContent}
              </Typography>
              {item.imageFileIDList?.length > 0 && (
                <Box display="grid" gridTemplateColumns="1fr 1fr" gridGap={10} mb={1}>
                  {item.imageFileIDList.map((x: any) => (
                    <img
                      key={x.id}
                      src={x.url ?? x?.imageFile?.url}
                      alt=""
                      style={{width: "100%", maxHeight: 300, objectFit: "contain"}}
                    />
                  ))}
                </Box>
              )}
              {item.answers?.map((answer: any, answerIndex: number) =>
                isEssay ? (
                  <Typography key={answerIndex} variant="body2">
                    {answer.answerContent}
                  </Typography>
                ) : (
                  <FormControlLabel
                    key={answerIndex}
                    control={<Radio disabled />}
                    label={answer.answerContent}
                  />
                )
              )}
            </Box>
          ))}

          <Box textAlign="right" mt={2}>
            <Button variant="contained" color="primary" onClick={onClose}>
              Đóng
            </Button>
          </Box>
        </Box>
      </Slide>
    </Modal>
  );
}
